import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { provinces, getDistricts } from "@/mocks/regionData";
import ImageUploader from "@/components/feature/ImageUploader";
import { useWeatherAutoFill } from "@/hooks/useWeatherAutoFill";
import { getCoordinates, fetchCurrentWeatherText } from "@/lib/weatherUtils";

const workTypes = ["칩핑", "파쇄", "상차", "정리", "정비", "기타"];
const weathers = ["맑음", "구름조금", "흐림", "비", "눈", "강풍"];

export default function NewReportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    siteName: "",
    workType: "",
    workDescription: "",
    workerCount: "",
    workHours: "",
    province: "",
    district: "",
    weather: "",
    workDate: new Date().toISOString().split("T")[0],
    remarks: ""
  });

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [weatherManuallyChanged, setWeatherManuallyChanged] = useState(false);
  const [regionWeatherLoading, setRegionWeatherLoading] = useState(false);
  const [regionLabel, setRegionLabel] = useState("");

  const { autoWeather, locationLabel, autoFillLoading, refetchWeather } = useWeatherAutoFill();

  // 초기 로드: 위젯 저장 지역으로 자동 입력
  useEffect(() => {
    if (autoWeather && !weatherManuallyChanged && !regionLabel) {
      setFormData(prev => ({ ...prev, weather: autoWeather }));
    }
  }, [autoWeather, weatherManuallyChanged, regionLabel]);

  // 폼 지역 변경 시: 해당 지역 날씨로 자동 업데이트
  useEffect(() => {
    if (!formData.province) {
      setRegionLabel("");
      return;
    }

    let cancelled = false;
    setRegionWeatherLoading(true);

    const label = formData.district
      ? `${formData.province} ${formData.district}`
      : formData.province;
    setRegionLabel(label);

    const fetchRegionWeather = async () => {
      try {
        const coords = await getCoordinates(formData.province, formData.district || "");
        if (!coords || cancelled) return;
        const weatherText = await fetchCurrentWeatherText(coords.latitude, coords.longitude);
        if (!cancelled && weatherText) {
          setFormData(prev => ({ ...prev, weather: weatherText }));
          setWeatherManuallyChanged(false);
        }
      } finally {
        if (!cancelled) setRegionWeatherLoading(false);
      }
    };

    fetchRegionWeather();
    return () => { cancelled = true; };
  }, [formData.province, formData.district]);

  const districts = getDistricts(formData.province);

  const [siteHistory, setSiteHistory] = useState<string[]>([]);
  const [showSiteSuggestions, setShowSiteSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const siteInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchSiteHistory = async () => {
      try {
        const data = await api.get<{ site_name: string }[]>("/reports");
        const uniqueSites = [...new Set(data.map((r) => r.site_name))];
        setSiteHistory(uniqueSites);
      } catch {
        // silently fail, autocomplete is not critical
      }
    };

    fetchSiteHistory();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        siteInputRef.current &&
        !siteInputRef.current.contains(e.target as Node)
      ) {
        setShowSiteSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSuggestions = siteHistory.filter(
    (site) =>
      site.toLowerCase().includes(formData.siteName.toLowerCase()) &&
      site.toLowerCase() !== formData.siteName.toLowerCase()
  );

  const handleSiteChange = (value: string) => {
    setFormData(prev => ({ ...prev, siteName: value }));
    setShowSiteSuggestions(true);
    setActiveSuggestionIndex(-1);
  };

  const handleSelectSuggestion = (site: string) => {
    setFormData(prev => ({ ...prev, siteName: site }));
    setShowSiteSuggestions(false);
    setActiveSuggestionIndex(-1);
    siteInputRef.current?.focus();
  };

  const handleSiteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSiteSuggestions || filteredSuggestions.length === 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setShowSiteSuggestions(true);
        setActiveSuggestionIndex(e.key === "ArrowDown" ? 0 : filteredSuggestions.length - 1);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case "Enter":
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < filteredSuggestions.length) {
          e.preventDefault();
          handleSelectSuggestion(filteredSuggestions[activeSuggestionIndex]);
        }
        break;
      case "Escape":
        setShowSiteSuggestions(false);
        setActiveSuggestionIndex(-1);
        break;
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => {
      if (field === "province" && prev.province !== value) {
        return { ...prev, [field]: value, district: "" };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError("");
    setLoading(true);

    try {
      await api.post("/reports", {
        site_name: formData.siteName,
        work_type: formData.workType,
        work_description: formData.workDescription,
        worker_count: parseInt(formData.workerCount, 10),
        work_hours: parseFloat(formData.workHours),
        region: `${formData.province}${formData.district ? ` ${formData.district}` : ""}`,
        weather: formData.weather,
        work_date: formData.workDate,
        remarks: formData.remarks,
        image_urls: imageUrls,
      });

      navigate("/reports");
    } catch {
      setError("일보 저장 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/reports");
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <button onClick={() => navigate("/reports")} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 cursor-pointer mb-3">
          <i className="ri-arrow-left-line"></i>
          작업일보 목록
        </button>
        <h1 className="text-xl font-bold text-gray-900">작업일보 작성</h1>
        <p className="text-sm text-gray-400 mt-0.5">오늘의 작업 내용을 기록하세요</p>
      </div>

      {error && (
        <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm"></i>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                현장명 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-building-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  ref={siteInputRef}
                  type="text"
                  value={formData.siteName}
                  onChange={(e) => handleSiteChange(e.target.value)}
                  onFocus={() => {
                    if (filteredSuggestions.length > 0 || (siteHistory.length > 0 && formData.siteName === "")) {
                      setShowSiteSuggestions(true);
                    }
                  }}
                  onKeyDown={handleSiteKeyDown}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="현장명을 입력하세요"
                  autoComplete="off"
                  required
                />
                {showSiteSuggestions && (
                  <div
                    ref={suggestionsRef}
                    className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
                  >
                    {(filteredSuggestions.length > 0
                      ? filteredSuggestions
                      : siteHistory.filter(s => s.toLowerCase() !== formData.siteName.toLowerCase())
                    ).slice(0, 10).map((site, index) => (
                      <div
                        key={site}
                        onClick={() => handleSelectSuggestion(site)}
                        className={`px-4 py-2.5 cursor-pointer transition-colors flex items-center gap-2.5 text-sm ${
                          index === activeSuggestionIndex
                            ? "bg-amber-50 text-amber-800"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                      >
                        <i className="ri-building-line text-gray-400 text-sm"></i>
                        <span>{site}</span>
                      </div>
                    ))}
                    {siteHistory.filter(s => s.toLowerCase() !== formData.siteName.toLowerCase()).length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">
                        {formData.siteName ? "일치하는 현장명이 없습니다" : "저장된 현장명이 없습니다"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업 유형 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-tools-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.workType}
                  onChange={(e) => handleChange("workType", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required
                >
                  <option value="">작업 유형 선택</option>
                  {workTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업일자 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-calendar-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="date"
                  value={formData.workDate}
                  onChange={(e) => handleChange("workDate", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                지역 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-map-pin-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.province}
                  onChange={(e) => handleChange("province", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required
                >
                  <option value="">시/도 선택</option>
                  {provinces.map((p: string) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                시/군/구 {districts.length > 0 && <span className="text-red-400">*</span>}
              </label>
              <div className="relative">
                <i className="ri-map-pin-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.district}
                  onChange={(e) => handleChange("district", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required={districts.length > 0}
                  disabled={districts.length === 0}
                >
                  <option value="">{districts.length === 0 ? "시/도 먼저 선택" : "시/군/구 선택"}</option>
                  {districts.map((d: string) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                날씨 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-cloud-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.weather}
                  onChange={(e) => {
                    handleChange("weather", e.target.value);
                    setWeatherManuallyChanged(true);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required
                >
                  <option value="">날씨 선택</option>
                  {weathers.map(weather => (
                    <option key={weather} value={weather}>{weather}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
{(regionLabel || locationLabel) && (
                <div className="flex items-center gap-1.5 mt-1.5 min-h-[18px]">
                  {(regionWeatherLoading || autoFillLoading) ? (
                    <>
                      <div className="w-3 h-3 border border-amber-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                      <span className="text-xs text-gray-400">날씨 검색 중...</span>
                    </>
                  ) : (
                    <>
                      <i className={`ri-map-pin-line text-xs flex-shrink-0 ${regionLabel ? "text-amber-500" : "text-gray-400"}`}></i>
                      <span className="text-xs text-gray-400 truncate">
                        {regionLabel || locationLabel} 현재 날씨 자동 입력
                      </span>
                      {!regionLabel && (
                        <button
                          type="button"
                          onClick={() => {
                            setWeatherManuallyChanged(false);
                            refetchWeather();
                          }}
                          className="ml-auto flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap flex-shrink-0"
                        >
                          <i className="ri-refresh-line text-xs"></i>
                          새로고침
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                투입 인원 (명) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-team-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="number"
                  min="1"
                  value={formData.workerCount}
                  onChange={(e) => handleChange("workerCount", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업 시간 (시간) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-time-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={formData.workHours}
                  onChange={(e) => handleChange("workHours", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업 내용 <span className="text-red-400">*</span>
              </label>
              <textarea
                value={formData.workDescription}
                onChange={(e) => handleChange("workDescription", e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                placeholder="작업 내용을 상세히 입력해주세요. (예: 사용 자재, 시공 방법, 작업 구역 등)"
                required
              ></textarea>
            </div>

            <div className="md:col-span-2">
              <ImageUploader imageUrls={imageUrls} onChange={setImageUrls} maxImages={5} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">특이사항</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                placeholder="특이사항, 문제점, 건의사항 등을 입력해주세요."
              ></textarea>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "저장 중..." : "일보 저장"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}